// src/contexts/AuthContext.js
import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth, db } from '../firebase';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  onAuthStateChanged,
  sendEmailVerification,
} from 'firebase/auth';
import {
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
  onSnapshot,
  updateDoc,
} from 'firebase/firestore';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [organisation, setOrganisation] = useState(null);          // ⭐
  const [userStatus, setUserStatus] = useState(null);
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(true);

  /* ────────────────────────────────────────────────────────── */
  /* 1) Register                                               */
  /* ────────────────────────────────────────────────────────── */
  async function register(email, password, nickname) {
    // Authentication
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const { uid } = userCredential.user;

    // Firestore
    await setDoc(doc(db, 'users', uid), {
      uid,
      email,
      avatarUrl: '',
      nickname: nickname || '',
      role: 'user',
      status: 'active',
      organisation: 'org20250507122326074083',        // ⭐ yeni kullanıcıda boş başlatıyoruz
      createdAt: serverTimestamp(),
      emailVerified: false,
    });

    await sendEmailVerification(userCredential.user);
    await signOut(auth);
    return userCredential;
  }

  async function resendVerificationEmail(user) {
    if (!user) {
      throw new Error('Missing user for verification email');
    }
    await sendEmailVerification(user);
  }

  /* ────────────────────────────────────────────────────────── */
  /* 2-4) Login, Reset, Logout                                 */
  /* ────────────────────────────────────────────────────────── */
  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }
  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }
  function logout() {
    return signOut(auth);
  }

  /* ────────────────────────────────────────────────────────── */
  /* 5) Auth state listener                                    */
  /* ────────────────────────────────────────────────────────── */
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // e-mail doğrulaması senkronizasyonu
        if (user.emailVerified) {
          const userDocRef = doc(db, 'users', user.uid);
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists() && !userDocSnap.data().emailVerified) {
            await updateDoc(userDocRef, { emailVerified: true });
          }
        }

        // Firestore'daki kullanıcıyı dinle
        const userDocRef = doc(db, 'users', user.uid);
        const unsubscribeUserDoc = onSnapshot(userDocRef, (snapshot) => {
          const userData = snapshot.exists() ? snapshot.data() : {};

          setCurrentUser({
            uid: user.uid,
            email: user.email,
            avatarUrl: userData.avatarUrl || '',
            nickname: userData.nickname || '',
            organisation: userData.organisation || '',             // ⭐
          });

          setOrganisation(userData.organisation || null);          // ⭐
          setUserRole(userData.role || 'user');
          setUserStatus(userData.status || 'active');
          setAuthed(true);
          setLoading(false);
        });

        return unsubscribeUserDoc;
      }

      // Kullanıcı çıkış yaptıysa
      setCurrentUser(null);
      setUserRole(null);
      setOrganisation(null);                                       // ⭐
      setUserStatus(null);
      setAuthed(false);
      setLoading(false);
    });

    return unsubscribeAuth;
  }, []);

  /* ────────────────────────────────────────────────────────── */
  /* Context'te dışarıya verdiğimiz değerler                   */
  /* ────────────────────────────────────────────────────────── */
  const value = {
    /* BASIC */
    authed,
    currentUser,
    userRole,
    organisation,   // ⭐ doğrudan { organisation } şeklinde kullanabilirsiniz
    userStatus,

    /* ACTIONS */
    setAuthed,
    register,
    login,
    resetPassword,
    logout,
    resendVerificationEmail,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
